import React, { useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import AddOutlined from '@mui/icons-material/AddOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import { AuthContext } from '../context/auth-context';
import {
  EmptyState,
  FormField,
  PageToolbar,
  StatusPill,
  ViewSwitcher,
} from '../ui';
import { bidActions, toBidSnapshot, writeStoredBid } from '../../store/bid';
import { statusLabel, statusTone } from '../../utils/bidStatus';
import { usePreference } from '../../Hooks/usePreference';
import { EM_DASH } from '../../utils/formatDate';
import BidFormDialog from './BidFormDialog';
import { useBidDivisions, useBidList } from './useBids';
import { useOpenLinkedProject } from './useOpenLinkedProject';
import { divisionLabel, formatBidDue, formatMonthDay } from './bidFormat';

const GROUP_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];
const PAGE_SIZES = [25, 50, 100];
const SELECTED_ROW = 'bid-row--selected';

/** "6 open bids", "1 won bid", "24 bids" (All). */
const countCaption = (count, group) => {
  const word = group === 'all' ? '' : `${group} `;
  return `${count} ${word}bid${count === 1 ? '' : 's'}`;
};

// Blank due dates sort after every real one.
const dueComparator = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.getTime() - b.getTime();
};

const BidsGridToolbar = () => {
  const { ft } = useTheme().palette;
  return (
    <GridToolbarContainer
      sx={{
        justifyContent: 'space-between',
        gap: 2,
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${ft.neutrals.borderLight}`,
      }}
    >
      <Typography variant="caption" sx={{ color: ft.neutrals.textSecondary }}>
        Sorted by bid due, soonest first
      </Typography>
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        placeholder="Search bids"
        inputProps={{ 'aria-label': 'Search bids' }}
        sx={{
          width: 360,
          p: 0,
          '& .MuiOutlinedInput-root': { height: 34, fontSize: 14 },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: ft.neutrals.fieldBorder,
          },
        }}
      />
    </GridToolbarContainer>
  );
};

const NoBidsOverlay = ({ group, action }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    }}
  >
    <EmptyState
      compact
      title={`No ${group === 'all' ? '' : `${group} `}bids`}
      action={action}
    />
  </Box>
);

const RowActions = ({ onEdit, onOpen }) => {
  const { ft } = useTheme().palette;
  const [anchorEl, setAnchorEl] = useState(null);
  const run = (action) => (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    action();
  };
  return (
    <>
      <Tooltip title="Row actions">
        <IconButton
          size="small"
          aria-label="Row actions"
          aria-haspopup="menu"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          sx={{ color: ft.neutrals.textSecondary }}
        >
          <MoreHorizOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(event) => {
          event.stopPropagation?.();
          setAnchorEl(null);
        }}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={run(onEdit)} sx={{ fontSize: 14 }}>
          Edit bid
        </MenuItem>
        <MenuItem onClick={run(onOpen)} sx={{ fontSize: 14 }}>
          Open overview
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * AllBidsPage — /bids (ticket #295, plan §4.9). Show chips and Division
 * filter (both remembered), a count, New bid, and the bid grid on the free
 * MUI DataGrid v5. Clicking a row selects that bid and opens its Overview.
 */
const AllBidsPage = () => {
  const { ft } = useTheme().palette;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const openProject = useOpenLinkedProject();
  const selectedBid = useSelector((state) => state.bid.selectedBid);

  const [group, setGroup] = usePreference('bids.list.group', 'open');
  const [divisionId, setDivisionId] = usePreference('bids.list.division', '');
  const [pageSize, setPageSize] = usePreference('bids.list.pageSize', 50);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editBid, setEditBid] = useState(null);

  const { divisions } = useBidDivisions();
  const { bids, isLoading, error, reload } = useBidList({
    group,
    divisionId: divisionId === '' ? undefined : divisionId,
  });

  const selectBid = (bid) => {
    const snapshot = toBidSnapshot(bid);
    dispatch(bidActions.selectBid(snapshot));
    writeStoredBid(userId, snapshot);
    navigate('/bids/overview');
  };

  const columns = useMemo(
    () => [
      {
        field: 'bidNumber',
        headerName: 'Bid #',
        width: 120,
        cellClassName: 'bid-cell--number',
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: ({ value }) => (
          <Tooltip title={value} enterDelay={400}>
            <Box
              component="span"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'division',
        headerName: 'Division',
        width: 130,
        valueGetter: ({ row }) => divisionLabel(row.division),
        cellClassName: 'bid-cell--muted',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        valueGetter: ({ row }) => statusLabel(row.status),
        renderCell: ({ row }) => (
          <StatusPill
            tone={statusTone(row.status)}
            label={statusLabel(row.status)}
          />
        ),
      },
      {
        field: 'bidDueAt',
        headerName: 'Bid due',
        type: 'dateTime',
        width: 190,
        valueGetter: ({ row }) =>
          row.bidDueAt ? new Date(row.bidDueAt) : null,
        sortComparator: dueComparator,
        cellClassName: 'bid-cell--number',
        renderCell: ({ row }) => {
          const due = formatBidDue(row.bidDueAt);
          if (!due) {
            return (
              <Box component="span" sx={{ color: ft.neutrals.textDisabled }}>
                {EM_DASH}
              </Box>
            );
          }
          return (
            <span>
              {due.date}{' '}
              <Box component="span" sx={{ color: ft.neutrals.textSecondary }}>
                · {due.time}
              </Box>
            </span>
          );
        },
      },
      {
        field: 'project',
        headerName: 'Linked project',
        width: 160,
        sortable: false,
        valueGetter: ({ row }) => row.project?.number ?? '',
        renderCell: ({ row }) =>
          row.project ? (
            <Link
              component="button"
              underline="hover"
              onClick={(event) => {
                event.stopPropagation();
                openProject(row.project);
              }}
              sx={{ fontSize: 14, fontWeight: ft.fontWeights.medium }}
            >
              {row.project.number}
            </Link>
          ) : (
            <Box component="span" sx={{ color: ft.neutrals.textDisabled }}>
              {EM_DASH}
            </Box>
          ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 100,
        valueGetter: ({ row }) =>
          row.updatedAt ? new Date(row.updatedAt) : null,
        valueFormatter: ({ value }) => formatMonthDay(value),
        cellClassName: 'bid-cell--muted',
      },
      {
        field: 'actions',
        headerName: '',
        width: 48,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        renderCell: ({ row }) => (
          <RowActions
            onEdit={() => setEditBid(row)}
            onOpen={() => selectBid(row)}
          />
        ),
      },
    ],
    // selectBid and openProject only depend on stable store/router/auth values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ft, openProject]
  );

  const newBidButton = (
    <Button
      variant="contained"
      startIcon={<AddOutlined />}
      onClick={() => setIsCreateOpen(true)}
    >
      New bid
    </Button>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        px: 3,
        py: 2,
        gap: 2,
      }}
    >
      <PageToolbar
        sx={{ px: 0, py: 0 }}
        filters={
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant="fieldLabel"
                sx={{ color: ft.neutrals.textBody }}
              >
                Show
              </Typography>
              <ViewSwitcher
                size="medium"
                ariaLabel="Show"
                options={GROUP_OPTIONS}
                value={group}
                onChange={setGroup}
              />
            </Box>
            <FormField label="Division" sx={{ width: 180 }}>
              <Select
                size="small"
                displayEmpty
                value={
                  divisions.some((d) => d.id === divisionId) ? divisionId : ''
                }
                onChange={(event) => setDivisionId(event.target.value)}
              >
                <MenuItem value="">All divisions</MenuItem>
                {divisions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {divisionLabel(d)}
                  </MenuItem>
                ))}
              </Select>
            </FormField>
          </>
        }
        actions={
          <>
            {!isLoading && !error && (
              <Typography
                variant="caption"
                sx={{ color: ft.neutrals.textSecondary, mr: 1 }}
              >
                {countCaption(bids.length, group)}
              </Typography>
            )}
            {newBidButton}
          </>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {error ? (
          <EmptyState
            title="The bids could not be loaded."
            description="Check your connection and try again."
            action={
              <Button variant="outlined" onClick={reload}>
                Try again
              </Button>
            }
          />
        ) : (
          <DataGrid
            rows={bids}
            columns={columns}
            getRowId={(row) => row.id}
            loading={isLoading}
            onRowClick={({ row }) => selectBid(row)}
            getRowClassName={({ id }) =>
              id === selectedBid?.id ? SELECTED_ROW : ''
            }
            initialState={{
              sorting: { sortModel: [{ field: 'bidDueAt', sort: 'asc' }] },
            }}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={PAGE_SIZES}
            rowHeight={56}
            headerHeight={48}
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            components={{
              Toolbar: BidsGridToolbar,
              NoRowsOverlay: NoBidsOverlay,
            }}
            componentsProps={{
              noRowsOverlay: {
                group,
                // New bid is offered where a new bid would appear.
                action:
                  group === 'open' || group === 'all'
                    ? newBidButton
                    : undefined,
              },
            }}
            sx={{
              height: '100%',
              border: `1px solid ${ft.neutrals.divider}`,
              borderRadius: `${ft.radius.card}px`,
              backgroundColor: ft.neutrals.white,
              fontSize: 14,
              color: ft.neutrals.textPrimary,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: ft.neutrals.headerRow,
                borderBottom: `1px solid ${ft.neutrals.divider}`,
                borderRadius: 0,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                ...ft.type.tableHeader,
                fontSize: ft.type.tableHeader.fontSize,
                color: ft.neutrals.textSecondary,
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': { px: 1.5 },
              '& .MuiDataGrid-cell': {
                borderBottomColor: ft.neutrals.borderLight,
              },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-row:hover': { backgroundColor: ft.neutrals.tile },
              [`& .${SELECTED_ROW}, & .${SELECTED_ROW}:hover`]: {
                backgroundColor: ft.primary.tint,
                fontWeight: ft.fontWeights.medium,
              },
              '& .bid-cell--number': { fontVariantNumeric: 'tabular-nums' },
              '& .bid-cell--muted': { color: ft.neutrals.textBody },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                { outline: 'none' },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
            }}
          />
        )}
      </Box>

      <BidFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      <BidFormDialog
        open={!!editBid}
        mode="edit"
        bid={editBid}
        onClose={() => setEditBid(null)}
        onSaved={reload}
      />
    </Box>
  );
};

export default AllBidsPage;
